import { Children, useMemo, useState } from "react";
import { View, Text, ScrollView, Platform, Linking, Image } from "react-native";
import {
  ArrowLeft,
  FileText,
  Globe,
  Info,
  MessageCircle,
  Trophy,
  MapPin,
  Users,
  Shield,
  Heart,
  Calendar,
  DollarSign,
  CheckCircle,
  Clock,
} from "lucide-react-native";
import type { LucideIcon } from "lucide-react-native";
import type { NavigationProp } from "@react-navigation/native";
import type { NativeStackScreenProps } from "@react-navigation/native-stack";
import { useOnboarding } from "../../../onboarding/OnboardingContext";
import { useCoverage } from "@src/packages/coverage/hooks/useCoverage";
import { usePatient } from "@src/packages/patients/hooks/usePatient";
import { EMBER } from "../../../onboarding/theme";
import type { CostsStackParamList, MainTabParamList } from "../../types";
import { useRegion, useTranslations } from "@src/packages/i18n";
import RegionGatePlaceholder from "../../components/RegionGatePlaceholder";
import GlassScreenLayout from "@louise/moon-ui/primitives/GlassScreenLayout";
import {
  CardSubtitleText,
  CompactActionButton,
  DialogIcon,
  CardSurface,
  CardTitleText,
  IconButton,
  MoonDetailRow,
  EmptyState,
  SectionTitle,
  SecondaryButton,
  StatusBadge,
  Tag,
  PrimaryButton,
  BackLink,
} from "@louise/moon-ui";
import { SCREEN_PADDING_TOP } from "@louise/moon-ui/theme/tokens";
import EligibilityBadge from "./EligibilityBadge";
import { GRANTS } from "./grants.data";
import type { Grant } from "./grants.types";
import { checkGrantEligibility, grantTreatmentLabel } from "./grants.utils";
import { useSmartBack } from "../../hooks/useSmartBack";
import { inferRegionCode } from "@src/packages/costs/estimates/locations";
import grantNotFoundIllustration from "../../../assets/images/grant-not-found-illustration.png";
import { SCREEN_TITLE_GRADIENT_BACKGROUND_STYLE } from "../../components/ScreenTitle";
import { colors, semanticColors } from "@louise/theme/tokens";

type Props = NativeStackScreenProps<CostsStackParamList, "GrantDetail">;

function Section({
  title,
  icon,
  children,
}: {
  title: string;
  icon: LucideIcon;
  children: React.ReactNode;
}) {
  return (
    <View style={{ gap: 10 }}>
      <SectionTitle icon={icon}>{title}</SectionTitle>
      {children}
    </View>
  );
}

function InsetGroup({ children }: { children: React.ReactNode }) {
  return (
    <CardSurface variant="panel" style={{ padding: 16 }}>
      {children}
    </CardSurface>
  );
}

function DetailGrid({
  children,
  minColumnWidth,
}: {
  children: React.ReactNode;
  minColumnWidth: number;
}) {
  return (
    <View
      style={{
        flexDirection: "row",
        flexWrap: "wrap",
        gap: 14,
      }}
    >
      {Children.map(children, (child, index) =>
        child ? (
          <View
            key={index}
            style={{
              flexGrow: 1,
              flexBasis: minColumnWidth,
              minWidth: minColumnWidth,
            }}
          >
            {child}
          </View>
        ) : null,
      )}
    </View>
  );
}

function TagList({ items }: { items: string[] }) {
  return (
    <View style={{ flexDirection: "row", flexWrap: "wrap", gap: 6 }}>
      {items.map((item) => (
        <Tag key={item} label={item} tone="neutral" shape="pill" />
      ))}
    </View>
  );
}

function buildGrantChatPrompt(grant: Grant) {
  return [
    "I want help understanding whether this fertility grant may be a fit and how to prepare an application. Ask clarifying questions before making assumptions about my eligibility, finances, insurance, or medical history.",
    `Grant: ${grant.name} (${grant.organization})`,
    `Award: ${grant.awardAmount}`,
    `Treatments covered: ${grant.treatmentsCovered.join(", ")}`,
    `How to apply: ${grant.howToApply}`,
    `Listed requirements: ${grant.requirements.join("; ")}`,
  ].join("\n");
}

export default function GrantDetailScreen({ navigation, route }: Props) {
  const region = useRegion();
  const t = useTranslations();
  const { grantId, returnTab } = route.params;
  const backLabel = returnTab ? t("you.shared.back") : t("costs.grants");
  const grant = useMemo(
    () => GRANTS.find((g) => g.id === grantId) ?? null,
    [grantId],
  );

  const { data: onboarding } = useOnboarding();
  const { data: patient } = usePatient();
  const coverage = useCoverage();
  const [failedFavicon, setFailedFavicon] = useState<string | null>(null);
  const rootNavigation =
    navigation.getParent<NavigationProp<MainTabParamList>>();
  const handleBack = useSmartBack({
    returnTab,
    fallbackTab: "Costs",
    fallbackScreen: "Grants",
  });

  const eligibility = useMemo(() => {
    if (!grant) return null;
    return checkGrantEligibility(grant, {
      state: inferRegionCode(
        patient?.addresses?.[0]?.country ?? onboarding?.country,
        patient?.addresses?.[0]?.postalCode ?? onboarding?.zipCode,
      ),
      age: onboarding?.age ?? null,
      hasFertilityCoverage: coverage?.fertilityCoverage?.hasCoverage ?? null,
    });
  }, [
    grant,
    onboarding?.country,
    onboarding?.zipCode,
    onboarding?.age,
    patient?.addresses,
    coverage?.fertilityCoverage?.hasCoverage,
  ]);

  if (!grant) {
    return (
      <GlassScreenLayout>
        <ScrollView
          contentContainerStyle={{
            flexGrow: 1,
            paddingHorizontal: 16,
            paddingTop: SCREEN_PADDING_TOP,
            paddingBottom: 100,
            maxWidth: 520,
            width: "100%",
            alignSelf: "center",
          }}
          showsVerticalScrollIndicator={false}
        >
          <BackLink
            label={backLabel}
            onPress={handleBack}
            style={{ marginBottom: 8 }}
          />
          <EmptyState
            testID="grant-not-found-state"
            illustration={
              <DialogIcon
                testID="grant-not-found-illustration"
                source={grantNotFoundIllustration}
                width={68}
                height={80}
              />
            }
            title="Grant not found"
            titleVariant="featured"
            density="compact"
            subtitle="We couldn't find that grant. Browse the grants list to see current opportunities."
            subtitleStyle={{
              maxWidth: 280,
            }}
            action={
              <CompactActionButton
                label="Back to grants"
                variant="primary"
                leadingIcon={ArrowLeft}
                onPress={handleBack}
              />
            }
            style={{ width: "100%", paddingVertical: 28 }}
          />
        </ScrollView>
      </GlassScreenLayout>
    );
  }

  const statusConfig = {
    open: { label: "Open", tone: "green" as const, icon: CheckCircle },
    upcoming: { label: "Upcoming", tone: "gold" as const, icon: Clock },
    closed: { label: "Closed", tone: "neutral" as const, icon: Calendar },
  }[grant.applicationStatus];

  const locationLabel =
    grant.eligibility.location === "national"
      ? "United States (nationwide)"
      : grant.eligibility.location.join(", ");

  const ageLabel =
    grant.eligibility.ageMin != null && grant.eligibility.ageMax != null
      ? `Ages ${grant.eligibility.ageMin}–${grant.eligibility.ageMax}`
      : grant.eligibility.ageMax != null
        ? `Under ${grant.eligibility.ageMax}`
        : grant.eligibility.ageMin != null
          ? `${grant.eligibility.ageMin}+`
          : null;

  const insuranceLabel = {
    any: "Any insurance status",
    no_fertility: "No fertility insurance coverage",
    none_required: "No insurance required",
  }[grant.eligibility.insuranceRequired];

  const citizenshipLabel = {
    us_citizen: "U.S. citizen",
    us_resident: "U.S. citizen or permanent resident",
    us_or_canada: "U.S. or Canadian resident",
  }[grant.eligibility.citizenshipRequired];

  const maritalLabel =
    grant.eligibility.maritalStatus === "married" ? "Legally married" : null;

  const diagnosisLabel = {
    infertility: "Infertility diagnosis required",
    cancer: "Cancer diagnosis required",
    cystic_fibrosis: "Cystic fibrosis diagnosis required",
    endometriosis: "Endometriosis diagnosis required",
    brca: "BRCA1 or BRCA2 mutation required",
    any: null,
  }[grant.eligibility.diagnosis];

  const specialGroupLabel = grant.eligibility.specialGroups
    ? {
        veterans: "Wounded veterans",
        lgbtq: "LGBTQ+ individuals",
        jewish: "Jewish community",
      }[grant.eligibility.specialGroups]
    : null;

  if (region === null) return null;
  if (region !== "US") return <RegionGatePlaceholder />;

  return (
    <GlassScreenLayout>
      <ScrollView
        contentContainerStyle={{
          paddingHorizontal: 16,
          paddingTop: SCREEN_PADDING_TOP,
          paddingBottom: 100,
          maxWidth: 520,
          width: "100%",
          alignSelf: "center",
        }}
        showsVerticalScrollIndicator={false}
      >
        {/* Back button */}
        <BackLink
          label={backLabel}
          onPress={handleBack}
          style={{ marginBottom: 8 }}
        />

        {/* Main card */}
        <CardSurface
          style={{
            paddingHorizontal: 24,
            paddingTop: 24,
            paddingBottom: 28,
            gap: 24,
          }}
        >
          {/* Header */}
          <View style={{ gap: 16, alignItems: "center" }}>
            {/* Status badges */}
            <View
              style={{
                flexDirection: "row",
                alignItems: "center",
                justifyContent: "center",
                gap: 8,
              }}
            >
              <StatusBadge
                label={statusConfig.label}
                tone={statusConfig.tone}
                icon={statusConfig.icon}
                shape="pill"
              />
              {eligibility && <EligibilityBadge status={eligibility} />}
            </View>

            {/* Logo, title, and organization */}
            <View
              style={{
                alignItems: "center",
                gap: 10,
                alignSelf: "stretch",
              }}
            >
              {failedFavicon === grant.favicon ? (
                <Globe
                  size={18}
                  color={EMBER.colors.textFaint}
                  strokeWidth={1.8}
                />
              ) : (
                <Image
                  source={{ uri: grant.favicon }}
                  onError={() => setFailedFavicon(grant.favicon)}
                  style={
                    {
                      width: 34,
                      height: 34,
                      borderRadius: 8,
                      opacity: 0.65,
                      ...(Platform.OS === "web" && {
                        filter: "grayscale(1)",
                      }),
                    } as Record<string, unknown>
                  }
                />
              )}
              <CardTitleText
                style={
                  {
                    fontSize: 26,
                    lineHeight: 32,
                    fontWeight: "700",
                    color: colors.deepBrown,
                    alignSelf: "stretch",
                    textAlign: "center",
                    ...(Platform.OS === "web" && {
                      ...SCREEN_TITLE_GRADIENT_BACKGROUND_STYLE,
                      WebkitBackgroundClip: "text",
                      WebkitTextFillColor: "transparent",
                      backgroundClip: "text",
                    }),
                  } as Record<string, unknown>
                }
              >
                {grant.name}
              </CardTitleText>
            </View>

            <CardSubtitleText
              style={{
                color: EMBER.colors.textFaint,
                marginTop: 0,
                textAlign: "center",
              }}
            >
              by {grant.organization}
            </CardSubtitleText>
          </View>

          {/* Award */}
          <InsetGroup>
            <View style={{ gap: 24 }}>
              <View style={{ gap: 10, alignItems: "center" }}>
                <View
                  style={{
                    alignSelf: "stretch",
                    alignItems: "center",
                    justifyContent: "center",
                    minHeight: 30,
                  }}
                >
                  <SectionTitle icon={Trophy}>Award</SectionTitle>
                  <IconButton
                    accessibilityLabel={`Visit ${grant.organization} website`}
                    size="sm"
                    surface="soft"
                    backgroundColor={semanticColors.action.quiet.background}
                    borderColor={semanticColors.border.glass}
                    borderWidth={1}
                    onPress={() => Linking.openURL(grant.website)}
                    style={{
                      position: "absolute",
                      right: 0,
                      top: 0,
                    }}
                  >
                    <Globe
                      size={15}
                      color={EMBER.colors.textMid}
                      strokeWidth={1.8}
                    />
                  </IconButton>
                </View>
                <CardTitleText
                  style={{
                    fontSize: 22,
                    lineHeight: 28,
                    fontWeight: "600",
                    color: EMBER.colors.textMid,
                    textAlign: "center",
                  }}
                >
                  {grant.awardAmount}
                </CardTitleText>
              </View>

              <View style={{ gap: 14 }}>
                <DetailGrid minColumnWidth={88}>
                  <MoonDetailRow
                    icon={Calendar}
                    label="Deadline"
                    value={grant.applicationDeadline ?? "No fixed deadline"}
                  />
                  <MoonDetailRow
                    icon={Clock}
                    label="Frequency"
                    value={grant.frequency}
                  />
                  <MoonDetailRow
                    icon={DollarSign}
                    label={t("costs.grant_detail.application_fee")}
                    value={
                      grant.applicationFee != null
                        ? `$${grant.applicationFee}`
                        : "Free"
                    }
                  />
                </DetailGrid>
              </View>
            </View>
          </InsetGroup>

          {/* Treatments Covered */}
          <Section title="Treatments covered" icon={Heart}>
            <TagList
              items={grant.treatmentsCovered.map((treatment) =>
                grantTreatmentLabel(treatment, t),
              )}
            />
          </Section>

          {/* How to Apply */}
          <Section title="How to apply" icon={FileText}>
            <Text
              className="font-dm-sans"
              style={{
                fontSize: 14,
                color: EMBER.colors.textMid,
                lineHeight: 22,
              }}
            >
              {grant.howToApply}
            </Text>
          </Section>

          {/* Eligibility */}
          <Section title="Eligibility" icon={Shield}>
            <DetailGrid minColumnWidth={128}>
              <MoonDetailRow
                icon={MapPin}
                label="Location"
                value={locationLabel}
              />
              {ageLabel && (
                <MoonDetailRow icon={Users} label="Age" value={ageLabel} />
              )}
              <MoonDetailRow
                icon={Shield}
                label="Insurance"
                value={insuranceLabel}
              />
              <MoonDetailRow
                icon={Globe}
                label="Citizenship"
                value={citizenshipLabel}
              />
              {maritalLabel && (
                <MoonDetailRow
                  icon={Heart}
                  label={t("costs.grant_detail.marital_status")}
                  value={maritalLabel}
                />
              )}
              {diagnosisLabel && (
                <MoonDetailRow
                  icon={FileText}
                  label="Diagnosis"
                  value={diagnosisLabel}
                />
              )}
              {specialGroupLabel && (
                <MoonDetailRow
                  icon={Users}
                  label={t("costs.grant_detail.special_group")}
                  value={specialGroupLabel}
                />
              )}
              {grant.eligibility.incomeMax != null && (
                <MoonDetailRow
                  icon={DollarSign}
                  label="Household income"
                  value={`Household income under $${grant.eligibility.incomeMax.toLocaleString()}`}
                />
              )}
            </DetailGrid>
          </Section>

          {/* Requirements */}
          <Section title="Requirements" icon={CheckCircle}>
            <View style={{ gap: 8 }}>
              {grant.requirements.map((req) => (
                <View
                  key={req}
                  style={{
                    flexDirection: "row",
                    alignItems: "flex-start",
                    gap: 8,
                  }}
                >
                  <CheckCircle
                    size={14}
                    color={EMBER.colors.green}
                    strokeWidth={2}
                    style={{ marginTop: 2 }}
                  />
                  <Text
                    className="font-dm-sans"
                    style={{
                      fontSize: 14,
                      color: EMBER.colors.textMid,
                      lineHeight: 20,
                      flex: 1,
                    }}
                  >
                    {req}
                  </Text>
                </View>
              ))}
            </View>
          </Section>

          {/* About */}
          <Section title="About" icon={Info}>
            <Text
              className="font-dm-sans"
              style={{
                fontSize: 14,
                color: EMBER.colors.textMid,
                lineHeight: 22,
              }}
            >
              {grant.longDescription}
            </Text>
          </Section>

          {/* Actions */}
          <View
            style={{
              flexDirection: "row",
              alignItems: "center",
              gap: 10,
              marginTop: 8,
            }}
          >
            <PrimaryButton
              flex={1.2}
              fullWidth={false}
              hapticStyle={null}
              label="Visit website"
              onPress={() => Linking.openURL(grant.website)}
              icon={
                <Globe
                  size={16}
                  color={semanticColors.action.primary.foreground}
                  strokeWidth={2}
                />
              }
            />
            <SecondaryButton
              flex={1}
              label="Chat about this"
              onPress={() =>
                rootNavigation?.navigate("Chat", {
                  screen: "Assistant",
                  params: {
                    initialPrompt: buildGrantChatPrompt(grant),
                    autoSendInitialPrompt: true,
                  },
                })
              }
              leading={
                <MessageCircle
                  size={16}
                  color={EMBER.colors.textMid}
                  strokeWidth={1.8}
                />
              }
            />
          </View>
        </CardSurface>
      </ScrollView>
    </GlassScreenLayout>
  );
}
